import { useMemo, useState } from 'react'
import { format, isValid, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import { CalendarIcon, XIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

type DatePickerInputProps = {
  id?: string
  value?: string | null
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function DatePickerInput({
  id,
  value,
  onChange,
  placeholder = 'Datum auswählen',
  className,
  disabled = false,
}: DatePickerInputProps) {
  const selectedDate = useMemo(() => {
    if (!value) return undefined
    const parsed = parseISO(value)
    return isValid(parsed) ? parsed : undefined
  }, [value])

  const [open, setOpen] = useState(false)
  const [visibleMonth, setVisibleMonth] = useState<Date>(selectedDate ?? new Date(1990, 0, 1))

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: currentYear - 1929 }, (_, index) => currentYear - index)
  }, [])

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: String(index),
        label: format(new Date(2024, index, 1), 'LLLL', { locale: de }),
      })),
    [],
  )

  const syncVisibleMonth = (nextOpen: boolean) => {
    if (nextOpen) {
      setVisibleMonth(selectedDate ?? new Date(1990, 0, 1))
    }
    setOpen(nextOpen)
  }

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={syncVisibleMonth}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-start pr-20 text-left font-normal hover:bg-background hover:text-foreground',
              !selectedDate && 'text-muted-foreground',
              className,
            )}
          >
            <span className="truncate">
              {selectedDate ? format(selectedDate, 'dd.MM.yyyy', { locale: de }) : placeholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[18rem] p-0" align="start">
          <div className="flex items-center gap-2 border-b px-3 py-3">
            <Select
              value={String(visibleMonth.getMonth())}
              onValueChange={(nextMonth) => {
                setVisibleMonth(
                  new Date(visibleMonth.getFullYear(), Number(nextMonth), 1),
                )
              }}
            >
              <SelectTrigger className="h-9 flex-1 bg-background">
                <SelectValue placeholder="Monat" />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={String(visibleMonth.getFullYear())}
              onValueChange={(nextYear) => {
                setVisibleMonth(
                  new Date(Number(nextYear), visibleMonth.getMonth(), 1),
                )
              }}
            >
              <SelectTrigger className="h-9 w-[7rem] bg-background">
                <SelectValue placeholder="Jahr" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {years.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Calendar
            mode="single"
            locale={de}
            selected={selectedDate}
            month={visibleMonth}
            onMonthChange={setVisibleMonth}
            onSelect={(date) => {
              onChange(date ? format(date, 'yyyy-MM-dd') : '')
              setOpen(false)
            }}
            className="p-3"
            classNames={{
              nav: 'hidden',
              month_caption: 'hidden',
            }}
            disabled={{ after: new Date() }}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-2">
        {selectedDate && !disabled ? (
          <button
            type="button"
            aria-label="Datum entfernen"
            className="pointer-events-auto rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onChange('')
              setOpen(false)
            }}
          >
            <XIcon className="h-4 w-4" />
          </button>
        ) : null}
        <CalendarIcon className="h-4 w-4 shrink-0 opacity-70" />
      </div>
    </div>
  )
}
